﻿import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useSocketStore } from './socket';
import { useToastStore } from './toast';
import { useAuthStore } from './auth';
import { startRinging, startRingback, stopRinging } from '../utils/ringTone';
import { getIceServers } from '../services/webrtcConfig';
import { tt } from '../utils/consoleText';

export interface Call {
  id: string;
  conversationId: string | null;
  callerId: string;
  calleeId: string;
  type: 'audio' | 'video';
  status: 'ringing' | 'active' | 'ended' | 'missed' | 'rejected';
  startedAt: Date;
  answeredAt: Date | null;
  endedAt: Date | null;
  duration: number | null;
  caller: {
    id: string;
    username: string;
    fullName: string | null;
    avatarImageId: string | null;
  };
  callee: {
    id: string;
    username: string;
    fullName: string | null;
    avatarImageId: string | null;
  };
}

export const useCallStore = defineStore('call', () => {
  const socketStore = useSocketStore();
  const authStore = useAuthStore();

  // State
  const activeCall = ref<Call | null>(null);
  const localStream = ref<MediaStream | null>(null);
  const remoteStream = ref<MediaStream | null>(null);
  const peerConnection = ref<RTCPeerConnection | null>(null);
  const callStatus = ref<'idle' | 'calling' | 'ringing' | 'active'>('idle');
  const isMuted = ref(false);
  const isVideoOff = ref(false);
  const error = ref<string | null>(null);
  const pendingOffer = ref<RTCSessionDescriptionInit | null>(null);
  // Human-readable name for the call target (e.g. panel name for panel calls)
  const callTargetName = ref<string | null>(null);

  // Computed
  const isInCall = computed(() => callStatus.value === 'active');
  const isOutgoingCall = computed(() => activeCall.value?.callerId === authStore.user?.id);
  const isIncomingCall = computed(() => activeCall.value?.calleeId === authStore.user?.id);
  const otherParticipant = computed(() => {
    if (!activeCall.value) return null;
    return isOutgoingCall.value ? activeCall.value.callee : activeCall.value.caller;
  });

  // ── Failures ────────────────────────────────────────────────────────────────
  // `error` is this flow's only failure channel: the two intercom surfaces hand
  // it their sentences (`IntercomCard.vue`, `IntercomContacts.vue`) and so do
  // `initiateCall` / `answerCall` and the hub's `people:error`. Nothing painted
  // the field — no component reads it — so a refused microphone was silent in
  // both languages. Raise it as a toast, the way `CamerasTab.vue` raises the
  // intercom store's own error, and consume it: the field is a one-shot
  // notification (the next attempt clears it in `initiateCall` anyway), so a
  // repeated identical failure paints again instead of being swallowed by the
  // watcher's value equality.
  const toastStore = useToastStore();
  watch(error, (msg) => {
    if (!msg) return;
    error.value = null;
    toastStore.addToast({ type: 'error', message: msg });
  });

  // Actions
  async function initiateCall(userId: string, type: 'audio' | 'video' = 'audio') {
    try {
      error.value = null;
      callStatus.value = 'calling';

      // Get user media
      localStream.value = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: type === 'video',
      });

      // Create peer connection with ICE servers from backend
      const iceServers = await getIceServers();
      peerConnection.value = new RTCPeerConnection({ iceServers });

      // Add local tracks to peer connection
      localStream.value.getTracks().forEach((track) => {
        peerConnection.value!.addTrack(track, localStream.value!);
      });

      // Handle remote stream
      peerConnection.value.ontrack = (event) => {
        if (!remoteStream.value) {
          remoteStream.value = new MediaStream();
        }
        event.streams[0].getTracks().forEach((track) => {
          remoteStream.value!.addTrack(track);
        });
      };

      // Handle ICE candidates
      peerConnection.value.onicecandidate = (event) => {
        if (event.candidate) {
          socketStore.socket?.emit('people:call:signal', {
            callId: activeCall.value?.id,
            signal: {
              type: 'ice-candidate',
              data: event.candidate,
            },
          });
        }
      };

      // Create offer
      const offer = await peerConnection.value.createOffer();
      await peerConnection.value.setLocalDescription(offer);

      // Send call initiation to server (include offer so callee receives it immediately)
      socketStore.socket?.emit('people:call:initiate', {
        calleeId: userId,
        type,
        offer,
      });

      // Play ringback tone for the caller while waiting
      startRingback();
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || tt('Failed to initiate call', 'Klica ni bilo mogoče vzpostaviti');
      console.error('Error initiating call:', err);
      await cleanup();
    }
  }

  async function answerCall(callId: string) {
    try {
      if (!activeCall.value || activeCall.value.id !== callId) {
        throw new Error(tt('No active call to answer', 'Ni dejavnega klica za odgovor'));
      }

      // Stop incoming ringtone immediately
      stopRinging();
      callStatus.value = 'active';

      // Get user media (may prompt for microphone permission on Android)
      try {
        localStream.value = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: activeCall.value.type === 'video',
        });
      } catch (__caught: unknown) {
        const mediaErr = __caught as Error;
        const msg =
          mediaErr.name === 'NotAllowedError' || mediaErr.name === 'PermissionDeniedError'
            ? tt(
                'Microphone permission denied. Please allow microphone access in app settings.',
                'Dostop do mikrofona je zavrnjen. Dovolite dostop do mikrofona v nastavitvah aplikacije.',
              )
            : tt('Could not access microphone.', 'Do mikrofona ni bilo mogoče dostopati.');
        throw new Error(msg);
      }

      // Create peer connection with ICE servers from backend
      const iceServers = await getIceServers();
      peerConnection.value = new RTCPeerConnection({ iceServers });

      // Add local tracks
      localStream.value.getTracks().forEach((track) => {
        peerConnection.value!.addTrack(track, localStream.value!);
      });

      // Handle remote stream
      peerConnection.value.ontrack = (event) => {
        if (!remoteStream.value) {
          remoteStream.value = new MediaStream();
        }
        event.streams[0].getTracks().forEach((track) => {
          remoteStream.value!.addTrack(track);
        });
      };

      // Handle ICE candidates
      peerConnection.value.onicecandidate = (event) => {
        if (event.candidate) {
          socketStore.socket?.emit('people:call:signal', {
            callId: activeCall.value?.id,
            signal: {
              type: 'ice-candidate',
              data: event.candidate,
            },
          });
        }
      };

      // If we have a pending offer from the caller, set remote description and create answer
      if (pendingOffer.value) {
        await peerConnection.value.setRemoteDescription(new RTCSessionDescription(pendingOffer.value));
        const answer = await peerConnection.value.createAnswer();
        await peerConnection.value.setLocalDescription(answer);
        pendingOffer.value = null;

        // Notify server (answer accepted)
        socketStore.socket?.emit('people:call:answer', { callId });

        // Send answer signal to caller
        socketStore.socket?.emit('people:call:signal', {
          callId,
          signal: {
            type: 'answer',
            data: answer,
          },
        });
      } else {
        // No offer yet — just notify server; offer will arrive via signal event
        socketStore.socket?.emit('people:call:answer', { callId });
      }
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || tt('Failed to answer call', 'Odgovarjanje na klic ni uspelo');
      console.error('Error answering call:', err);
      await cleanup();
    }
  }

  async function rejectCall(callId: string) {
    try {
      stopRinging();
      socketStore.socket?.emit('people:call:reject', { callId });
      await cleanup();
    } catch (err) {
      console.error('Error rejecting call:', err);
    }
  }

  async function endCall() {
    try {
      stopRinging();
      if (activeCall.value) {
        socketStore.socket?.emit('people:call:end', { callId: activeCall.value.id });
      }
      await cleanup();
    } catch (err) {
      console.error('Error ending call:', err);
    }
  }

  function toggleMute() {
    if (localStream.value) {
      const audioTrack = localStream.value.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        isMuted.value = !audioTrack.enabled;
      }
    }
  }

  function toggleVideo() {
    if (localStream.value) {
      const videoTrack = localStream.value.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        isVideoOff.value = !videoTrack.enabled;
      }
    }
  }

  async function cleanup() {
    // Stop any active ring/ringback tone
    stopRinging();

    // Stop local stream
    if (localStream.value) {
      localStream.value.getTracks().forEach((track) => track.stop());
      localStream.value = null;
    }

    // Stop remote stream
    if (remoteStream.value) {
      remoteStream.value.getTracks().forEach((track) => track.stop());
      remoteStream.value = null;
    }

    // Close peer connection
    if (peerConnection.value) {
      peerConnection.value.close();
      peerConnection.value = null;
    }

    activeCall.value = null;
    callStatus.value = 'idle';
    isMuted.value = false;
    isVideoOff.value = false;
    pendingOffer.value = null;
    callTargetName.value = null;
  }

  // Socket event handlers
  function setupSocketListeners() {
    // Call initiated successfully
    socketStore.on('people:call:initiated', (call: Call) => {
      activeCall.value = call;
      callStatus.value = 'calling';
    });

    // Incoming call (may include SDP offer from caller)
    socketStore.on('people:call:incoming', (data: Call & { offer?: RTCSessionDescriptionInit }) => {
      const { offer, ...call } = data;
      activeCall.value = call;
      callStatus.value = 'ringing';
      if (offer) {
        pendingOffer.value = offer;
      }
      startRinging();
    });

    // Call answered
    socketStore.on('people:call:answered', (call: Call) => {
      activeCall.value = call;
      callStatus.value = 'active';
      stopRinging();
    });

    // Call rejected
    socketStore.on('people:call:rejected', () => {
      stopRinging();
      cleanup();
    });

    // Call ended
    socketStore.on('people:call:ended', () => {
      stopRinging();
      cleanup();
    });

    // Call missed
    socketStore.on('people:call:missed', () => {
      stopRinging();
      cleanup();
    });

    // WebRTC signaling
    /**
     * One signalling step: an offer/answer description or a single ICE
     * candidate. Every field is optional on both, so the same `data` serves
     * `RTCSessionDescription` and `RTCIceCandidate` below.
     */
    interface CallSignal {
      type: string;
      data: RTCSessionDescriptionInit & RTCIceCandidateInit;
    }
    socketStore.on('people:call:signal', async (data: { callId: string; signal: CallSignal }) => {
      if (activeCall.value?.id !== data.callId) {
        return;
      }

      // If no peer connection yet (still ringing), store the offer for later
      if (!peerConnection.value) {
        if (data.signal.type === 'offer') {
          pendingOffer.value = data.signal.data;
        }
        return;
      }

      try {
        const { signal } = data;

        if (signal.type === 'offer') {
          // Callee receives offer
          await peerConnection.value.setRemoteDescription(new RTCSessionDescription(signal.data));

          // Create answer
          const answer = await peerConnection.value.createAnswer();
          await peerConnection.value.setLocalDescription(answer);

          // Send answer
          socketStore.socket?.emit('people:call:signal', {
            callId: activeCall.value?.id,
            signal: {
              type: 'answer',
              data: answer,
            },
          });
        } else if (signal.type === 'answer') {
          // Caller receives answer
          await peerConnection.value.setRemoteDescription(new RTCSessionDescription(signal.data));
        } else if (signal.type === 'ice-candidate') {
          // Both parties receive ICE candidates
          await peerConnection.value.addIceCandidate(new RTCIceCandidate(signal.data));
        }
      } catch (err) {
        console.error('Error handling call signal:', err);
        error.value = tt('Connection error occurred', 'Prišlo je do napake v povezavi');
      }
    });

    // Error from server
    socketStore.on('people:error', (data: { message: string }) => {
      error.value = data.message;
      if (callStatus.value !== 'idle') {
        cleanup();
      }
    });
  }

  let initialized = false;

  function initialize() {
    if (initialized) return;
    initialized = true;
    setupSocketListeners();
  }

  return {
    // State
    activeCall,
    peerConnection,
    localStream,
    remoteStream,
    callStatus,
    isMuted,
    isVideoOff,
    error,
    callTargetName,

    // Computed
    isInCall,
    isOutgoingCall,
    isIncomingCall,
    otherParticipant,

    // Actions
    initiateCall,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    cleanup,
    initialize,
  };
});
